// Curated European retreats & destinations.
// Each card links out to a real partner site for booking/discovery.

export const CATEGORIES = [
  { id: "all", label: "All Escapes", overline: "00" },
  { id: "travel", label: "Travel Destinations", overline: "01" },
  { id: "yoga", label: "Yoga Retreats", overline: "02" },
  { id: "wellness", label: "Wellness & Party", overline: "03" },
  { id: "adventure", label: "Adventure", overline: "04" },
];

// Helper builders for partner search URLs
const booking = (q) =>
  `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(q)}`;
const airbnb = (q) =>
  `https://www.airbnb.com/s/${encodeURIComponent(q)}/homes`;
const bookYoga = (q) =>
  `https://www.bookretreats.com/s/yoga-retreats/${encodeURIComponent(q.toLowerCase().replace(/\s+/g, "-"))}`;
const getYourGuide = (q) =>
  `https://www.getyourguide.com/en-us/s/?q=${encodeURIComponent(q)}`;

export const DESTINATIONS = [
  // ---------- TRAVEL DESTINATIONS ----------
  {
    id: "santorini",
    category: "travel",
    title: "Santorini",
    country: "Greece",
    duration: "5–7 days",
    priceFrom: "€780",
    blurb: "Whitewashed villages cascading down volcanic cliffs into the Aegean.",
    image: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Santorini%20Greece",
  },
  {
    id: "amalfi",
    category: "travel",
    title: "Amalfi Coast",
    country: "Italy",
    duration: "6–8 days",
    priceFrom: "€920",
    blurb: "Dramatic coastline of lemon groves, pastel villages and Mediterranean blue.",
    image: "https://images.unsplash.com/photo-1533676802871-eca1ae998cd5?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Amalfi%20Coast%20Italy",
  },
  {
    id: "swiss-alps",
    category: "travel",
    title: "Swiss Alps",
    country: "Switzerland",
    duration: "5–10 days",
    priceFrom: "€1,150",
    blurb: "Cog railways, glacial lakes and chalet villages tucked under jagged peaks.",
    image: "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Swiss%20Alps%20Switzerland",
  },
  {
    id: "tuscany",
    category: "travel",
    title: "Tuscany",
    country: "Italy",
    duration: "7 days",
    priceFrom: "€690",
    blurb: "Rolling vineyards, cypress avenues and Renaissance hill towns.",
    image: "https://images.unsplash.com/photo-1608476037397-7b53ace4c871?q=80&w=1200&auto=format&fit=crop",
    partner: "Intrepid Travel",
    link: "https://www.intrepidtravel.com/en/search?keywords=Castello%20di%20Casole%20Tuscany%20Italy",
  },
  {
    id: "provence",
    category: "travel",
    title: "Provence",
    country: "France",
    duration: "5 days",
    priceFrom: "€720",
    blurb: "Lavender fields, stone farmhouses and slow Mediterranean rhythm.",
    image: "https://images.unsplash.com/photo-1499678329028-101435549a4e?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Provence%20France",
  },
  {
    id: "cinque-terre",
    category: "travel",
    title: "Cinque Terre",
    country: "Italy",
    duration: "4 days",
    priceFrom: "€560",
    blurb: "Five candy-coloured fishing villages strung along the Ligurian cliffs.",
    image: "https://images.unsplash.com/photo-1543429776-2782fc8e1acd?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Cinque%20Terre%20Italy",
  },
  {
    id: "paris",
    category: "travel",
    title: "Paris",
    country: "France",
    duration: "4 days",
    priceFrom: "€640",
    blurb: "Boulevards, bistros and the eternal pull of the Seine at dusk.",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Paris%20France",
  },
  {
    id: "barcelona",
    category: "travel",
    title: "Barcelona",
    country: "Spain",
    duration: "5 days",
    priceFrom: "€590",
    blurb: "Gaudí curves, tapas at midnight and golden sand five minutes from the Gothic Quarter.",
    image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Barcelona%20Spain",
  },
  {
    id: "lisbon",
    category: "travel",
    title: "Lisbon",
    country: "Portugal",
    duration: "5 days",
    priceFrom: "€540",
    blurb: "Pastel tiles, yellow trams and fado drifting out of tiny taverns.",
    image: "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Lisbon%20Portugal",
  },
  {
    id: "prague",
    category: "travel",
    title: "Prague",
    country: "Czech Republic",
    duration: "4 days",
    priceFrom: "€460",
    blurb: "Gothic spires, hidden jazz cellars and the slow turn of the Charles Bridge crowd.",
    image: "https://images.unsplash.com/photo-1541849546-216549ae216d?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Prague%20Czech%20Republic",
  },
  {
    id: "vienna",
    category: "travel",
    title: "Vienna",
    country: "Austria",
    duration: "4 days",
    priceFrom: "€620",
    blurb: "Imperial coffeehouses, opera houses and slow Sunday strolls in Stadtpark.",
    image: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Vienna%20Austria",
  },
  {
    id: "dubrovnik",
    category: "travel",
    title: "Dubrovnik",
    country: "Croatia",
    duration: "5 days",
    priceFrom: "€720",
    blurb: "Marble streets inside medieval walls dropping into Adriatic blue.",
    image: "https://images.unsplash.com/photo-1604147706283-d7119b5b822c?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Dubrovnik%20Croatia",
  },
  {
    id: "lake-como",
    category: "travel",
    title: "Lake Como",
    country: "Italy",
    duration: "4 days",
    priceFrom: "€880",
    blurb: "Cliffside villas, deep alpine water and lazy lakeside aperitivos.",
    image: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Lake%20Como%20Italy",
  },
  {
    id: "venice",
    category: "travel",
    title: "Venice",
    country: "Italy",
    duration: "3 days",
    priceFrom: "€690",
    blurb: "Canal-lit mornings, hidden cicchetti bars and pure architectural theatre.",
    image: "https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Venice%20Italy",
  },
  {
    id: "bruges",
    category: "travel",
    title: "Bruges",
    country: "Belgium",
    duration: "3 days",
    priceFrom: "€420",
    blurb: "Storybook canals, lace shops and the best chocolate in Northern Europe.",
    image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Bruges%20Belgium",
  },
  {
    id: "copenhagen",
    category: "travel",
    title: "Copenhagen",
    country: "Denmark",
    duration: "4 days",
    priceFrom: "€780",
    blurb: "Coloured townhouses, harbour swims and Nordic-cool design everywhere.",
    image: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Copenhagen%20Denmark",
  },
  {
    id: "mont-saint-michel",
    category: "travel",
    title: "Mont Saint-Michel",
    country: "Normandy, France",
    duration: "2 days",
    priceFrom: "€380",
    blurb: "Tidal abbey rising out of the Atlantic — France at its most cinematic.",
    image: "https://images.unsplash.com/photo-1591197172062-c718f82aba20?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Mont%20Saint-Michel%20Normandy%2C%20France",
  },
  {
    id: "edinburgh",
    category: "travel",
    title: "Edinburgh",
    country: "Scotland",
    duration: "4 days",
    priceFrom: "€520",
    blurb: "Old Town wynds, whisky bars and Arthur's Seat at sunset.",
    image: "https://images.unsplash.com/photo-1531572753322-ad063cecc140?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Edinburgh%20Scotland",
  },
  {
    id: "mallorca-town",
    category: "travel",
    title: "Palma de Mallorca",
    country: "Spain",
    duration: "5 days",
    priceFrom: "€610",
    blurb: "A working Mediterranean city with a Gothic cathedral and tucked-away coves.",
    image: "https://images.unsplash.com/photo-1592194996308-7b43878e84a6?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Palma%20de%20Mallorca%20Spain",
  },
  {
    id: "reykjavik",
    category: "travel",
    title: "Reykjavík",
    country: "Iceland",
    duration: "4 days",
    priceFrom: "€820",
    blurb: "Geothermal pools, brutalist concrete and Northern Lights on clear nights.",
    image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Reykjav%C3%ADk%20Iceland",
  },

  // ---------- YOGA RETREATS ----------
  {
    id: "andalusia-yoga",
    category: "yoga",
    title: "Mountain View Yoga",
    country: "Andalusia, Spain",
    duration: "7 days",
    priceFrom: "€890",
    blurb: "Sunrise asana on a sierra ridge, with daily silent walks and Spanish soul food.",
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-andalusia-spain",
  },
  {
    id: "algarve-yoga",
    category: "yoga",
    title: "Coastal Mindfulness",
    country: "Algarve, Portugal",
    duration: "8 days",
    priceFrom: "€1,020",
    blurb: "Ocean-cliff vinyasa, breathwork at dawn and surf-and-stretch afternoons.",
    image: "https://images.unsplash.com/photo-1512438248247-f0f2a5a8b7f0?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=8-day-surf-and-yoga-retreat-algarve-portugal",
  },
  {
    id: "black-forest-yoga",
    category: "yoga",
    title: "Forest Healing",
    country: "Black Forest, Germany",
    duration: "5 days",
    priceFrom: "€780",
    blurb: "Restorative yoga and forest bathing immersed in old pine canopy.",
    image: "https://images.unsplash.com/photo-1529693662653-9d480530a697?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=5-day-forest-yoga-retreat-black-forest-germany",
  },
  {
    id: "tuscan-villa-yoga",
    category: "yoga",
    title: "Tuscan Villa Retreat",
    country: "Chianti, Italy",
    duration: "7 days",
    priceFrom: "€1,180",
    blurb: "Morning vinyasa among olive trees, evening wine tastings, slow living.",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-villa-yoga-retreat-tuscany-italy",
  },
  {
    id: "swiss-silence",
    category: "yoga",
    title: "Silent Alpine Retreat",
    country: "Engadin, Switzerland",
    duration: "10 days",
    priceFrom: "€1,640",
    blurb: "Ten days of noble silence at altitude. Meditation, journaling, glaciers.",
    image: "https://images.unsplash.com/photo-1593810450967-f9c42742e326?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=10-day-silent-meditation-retreat-swiss-alps",
  },
  {
    id: "ibiza-holistic",
    category: "yoga",
    title: "Ibiza Holistic Week",
    country: "Ibiza, Spain",
    duration: "7 days",
    priceFrom: "€1,290",
    blurb: "Kundalini, sound healing and cacao on the island's quiet northern coast.",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-holistic-yoga-retreat-ibiza-spain",
  },
  {
    id: "crete-yoga",
    category: "yoga",
    title: "Cretan Sun Salutations",
    country: "Crete, Greece",
    duration: "7 days",
    priceFrom: "€980",
    blurb: "Olive-grove yoga shala, Mediterranean diet and sea-cave swims.",
    image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-crete-greece",
  },
  {
    id: "madeira-yoga",
    category: "yoga",
    title: "Madeira Island Reset",
    country: "Madeira, Portugal",
    duration: "8 days",
    priceFrom: "€1,180",
    blurb: "Cloud-forest hikes, levada walks and yin yoga over the Atlantic.",
    image: "https://images.unsplash.com/photo-1560662105-57f8ad6ae2d1?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=8-day-yoga-retreat-madeira-portugal",
  },
  {
    id: "sicily-yoga",
    category: "yoga",
    title: "Sicilian Slow Living",
    country: "Sicily, Italy",
    duration: "7 days",
    priceFrom: "€1,090",
    blurb: "Volcanic vinyasa with Etna in the distance and Sicilian feasts at sunset.",
    image: "https://images.unsplash.com/photo-1563804447971-6e113ab80713?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-sicily-italy",
  },
  {
    id: "lake-bled-yoga",
    category: "yoga",
    title: "Lake Bled Yin",
    country: "Bled, Slovenia",
    duration: "5 days",
    priceFrom: "€820",
    blurb: "Alpine-lake mornings, forest bathing and yin yoga on the chapel island.",
    image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=5-day-yin-yoga-retreat-bled-slovenia",
  },
  {
    id: "santorini-yoga",
    category: "yoga",
    title: "Santorini Caldera Flow",
    country: "Santorini, Greece",
    duration: "6 days",
    priceFrom: "€1,240",
    blurb: "Sunset asana from a cave studio overlooking the caldera.",
    image: "https://images.unsplash.com/photo-1540541338287-41700207dee6?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=6-day-yoga-retreat-santorini-greece",
  },
  {
    id: "mallorca-yoga",
    category: "yoga",
    title: "Tramuntana Mindfulness",
    country: "Mallorca, Spain",
    duration: "7 days",
    priceFrom: "€1,050",
    blurb: "Stone finca, mountain views and twice-daily Hatha plus meditation.",
    image: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-mallorca-tramuntana-spain",
  },
  {
    id: "sardinia-yoga",
    category: "yoga",
    title: "Sardinia Sea Retreat",
    country: "Sardinia, Italy",
    duration: "8 days",
    priceFrom: "€1,180",
    blurb: "Granite coves, daily SUP yoga and pristine emerald sea swims.",
    image: "https://images.unsplash.com/photo-1522163182402-834f871fd851?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=8-day-yoga-retreat-sardinia-italy",
  },
  {
    id: "lake-garda-yoga",
    category: "yoga",
    title: "Lake Garda Wellness",
    country: "Lake Garda, Italy",
    duration: "5 days",
    priceFrom: "€860",
    blurb: "Lemon-grove studio, lake swims and Italian wellness cuisine.",
    image: "https://images.unsplash.com/photo-1534447677768-be436bb09401?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=5-day-wellness-retreat-lake-garda-italy",
  },
  {
    id: "azores-yoga",
    category: "yoga",
    title: "Azores Volcanic Calm",
    country: "São Miguel, Azores",
    duration: "7 days",
    priceFrom: "€1,160",
    blurb: "Crater-lake meditation, thermal pools and quiet Atlantic mornings.",
    image: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-azores-portugal",
  },
  {
    id: "cyprus-yoga",
    category: "yoga",
    title: "Cyprus Coastal Reset",
    country: "Paphos, Cyprus",
    duration: "7 days",
    priceFrom: "€990",
    blurb: "Sun-soaked vinyasa above the Mediterranean and Cypriot mezze nights.",
    image: "https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-paphos-cyprus",
  },
  {
    id: "malta-yoga",
    category: "yoga",
    title: "Maltese Stone Sanctuary",
    country: "Gozo, Malta",
    duration: "6 days",
    priceFrom: "€940",
    blurb: "Honey-stone farmhouse, sea-cliff meditations and ancient temples.",
    image: "https://images.unsplash.com/photo-1501555088652-021faa106b9b?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=6-day-yoga-retreat-gozo-malta",
  },
  {
    id: "french-riviera-yoga",
    category: "yoga",
    title: "Côte d'Azur Yoga",
    country: "Nice, France",
    duration: "5 days",
    priceFrom: "€1,120",
    blurb: "Cliffside studio, Provençal cuisine and slow Riviera afternoons.",
    image: "https://images.unsplash.com/photo-1591389703635-e15a07b842d7?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=5-day-yoga-retreat-nice-french-riviera",
  },
  {
    id: "costa-brava-yoga",
    category: "yoga",
    title: "Costa Brava Soul Week",
    country: "Catalonia, Spain",
    duration: "7 days",
    priceFrom: "€990",
    blurb: "Pine-cliff coves, cacao ceremonies and barefoot Catalan dinners.",
    image: "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=7-day-yoga-retreat-costa-brava-spain",
  },
  {
    id: "iceland-yoga",
    category: "yoga",
    title: "Icelandic Hot Spring Retreat",
    country: "South Iceland",
    duration: "6 days",
    priceFrom: "€1,540",
    blurb: "Geothermal bathing, lava-field walks and aurora meditation.",
    image: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=1200&auto=format&fit=crop",
    partner: "BookRetreats",
    link: "https://www.bookretreats.com/search?query=6-day-hot-spring-yoga-retreat-iceland",
  },

  // ---------- WELLNESS & PARTY ----------
  {
    id: "mykonos-beachclub",
    category: "wellness",
    title: "Mykonos Beach Club",
    country: "Mykonos, Greece",
    duration: "4 days",
    priceFrom: "€1,150",
    blurb: "Pilates by the sea, juice cleanses by day, world-class DJs by night.",
    image: "https://images.unsplash.com/photo-1700420242966-7e201c7283eb?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Mykonos%20Scorpios%20Beach%20Club%20Day%20Pass",
  },
  {
    id: "ibiza-sunset",
    category: "wellness",
    title: "Ibiza Sunset Sessions",
    country: "Ibiza, Spain",
    duration: "5 days",
    priceFrom: "€990",
    blurb: "Café del Mar terraces, white-sand mornings and open-air dancing till dawn.",
    image: "https://images.unsplash.com/photo-1545389336-cf090694435e?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Ibiza%20Cafe%20del%20Mar%20Sunset%20Boat%20Party",
  },
  {
    id: "budapest-baths",
    category: "wellness",
    title: "Thermal Bath Sparties",
    country: "Budapest, Hungary",
    duration: "3 days",
    priceFrom: "€420",
    blurb: "Century-old neo-baroque baths transformed into late-night thermal raves.",
    image: "https://images.unsplash.com/photo-1523906921802-b5d2d899e93b?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Szechenyi%20Thermal%20Bath%20Sparty%20Budapest",
  },
  {
    id: "sttropez-yacht",
    category: "wellness",
    title: "St. Tropez Yacht Life",
    country: "Côte d'Azur, France",
    duration: "5 days",
    priceFrom: "€2,180",
    blurb: "Sunrise yoga on deck, Provençal lunches and golden-hour boat parties.",
    image: "https://images.unsplash.com/photo-1490682143684-14369e18dce8?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=St.%20Tropez%20Yacht%20Life%20C%C3%B4te%20d%27Azur%2C%20France",
  },
  {
    id: "hvar-croatia",
    category: "wellness",
    title: "Hvar Island Vibe",
    country: "Hvar, Croatia",
    duration: "6 days",
    priceFrom: "€880",
    blurb: "Crystal coves, superfood lunches and electric nights at Carpe Diem.",
    image: "https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Hvar%20Island%20Vibe%20Hvar%2C%20Croatia",
  },
  {
    id: "marbella-resort",
    category: "wellness",
    title: "Marbella Resort Weekend",
    country: "Costa del Sol, Spain",
    duration: "3 days",
    priceFrom: "€620",
    blurb: "Spa rituals by morning, beach club champagne brunches at sunset.",
    image: "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Marbella%20Resort%20Weekend%20Costa%20del%20Sol%2C%20Spain",
  },
  {
    id: "berlin-techno",
    category: "wellness",
    title: "Berlin Techno + Sauna",
    country: "Berlin, Germany",
    duration: "3 days",
    priceFrom: "€480",
    blurb: "Friday-night Berghain, Saturday-morning bathhouse, Sunday-evening park.",
    image: "https://images.unsplash.com/photo-1531058020387-3be344556be6?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Berlin%20Berghain%20Tresor%20Club%20Tour",
  },
  {
    id: "amsterdam-spa",
    category: "wellness",
    title: "Amsterdam Canal Wellness",
    country: "Amsterdam, Netherlands",
    duration: "3 days",
    priceFrom: "€520",
    blurb: "Floating sauna boats by morning, jazz cellars by night.",
    image: "https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Amsterdam%20Canal%20Wellness%20Amsterdam%2C%20Netherlands",
  },
  {
    id: "barcelona-beachclub",
    category: "wellness",
    title: "Barceloneta Beach Club",
    country: "Barcelona, Spain",
    duration: "4 days",
    priceFrom: "€720",
    blurb: "Day-beds at the water, vermouth hour, and Razzmatazz till dawn.",
    image: "https://images.unsplash.com/photo-1530021232320-687d8e3dba54?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Barcelona%20Pacha%20Opium%20Beach%20Club%20Pass",
  },
  {
    id: "lisbon-nights",
    category: "wellness",
    title: "Lisbon Bairro Alto Nights",
    country: "Lisbon, Portugal",
    duration: "4 days",
    priceFrom: "€560",
    blurb: "Rooftop bars in Príncipe Real, ginja in tiny taverns, sunrise at Miradouro.",
    image: "https://images.unsplash.com/photo-1543783207-ec64e4d95325?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Lisbon%20Bairro%20Alto%20Pub%20Crawl%20Fado%20Night",
  },
  {
    id: "santorini-sunset",
    category: "wellness",
    title: "Santorini Sunset Lounges",
    country: "Santorini, Greece",
    duration: "4 days",
    priceFrom: "€1,180",
    blurb: "Caldera-edge spas, infinity pools and Oia at golden hour.",
    image: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Santorini%20Sunset%20Lounges%20Santorini%2C%20Greece",
  },
  {
    id: "vienna-thermae",
    category: "wellness",
    title: "Vienna Thermal Weekend",
    country: "Vienna, Austria",
    duration: "3 days",
    priceFrom: "€560",
    blurb: "Therme Wien spa, late-night cocktails in MuseumsQuartier.",
    image: "https://images.unsplash.com/photo-1533676802871-eca1ae998cd5?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Vienna%20Thermal%20Weekend%20Vienna%2C%20Austria",
  },
  {
    id: "madrid-roof",
    category: "wellness",
    title: "Madrid Rooftop Reset",
    country: "Madrid, Spain",
    duration: "3 days",
    priceFrom: "€480",
    blurb: "Morning yoga over Gran Vía, late nights in Malasaña.",
    image: "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Madrid%20Rooftop%20Reset%20Madrid%2C%20Spain",
  },
  {
    id: "crete-spa",
    category: "wellness",
    title: "Cretan Olive Spa",
    country: "Crete, Greece",
    duration: "5 days",
    priceFrom: "€940",
    blurb: "Aegean spa rituals, mountain trekking and family-style tavernas.",
    image: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Cretan%20Olive%20Spa%20Crete%2C%20Greece",
  },
  {
    id: "mallorca-bcm",
    category: "wellness",
    title: "Magaluf Beach Sessions",
    country: "Mallorca, Spain",
    duration: "4 days",
    priceFrom: "€680",
    blurb: "Day-club pool parties, BCM nights and morning sea swims.",
    image: "https://images.unsplash.com/photo-1499678329028-101435549a4e?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Magaluf%20BCM%20Planet%20Dance%20Mallorca%20Pass",
  },
  {
    id: "stockholm-sauna",
    category: "wellness",
    title: "Stockholm Archipelago Sauna",
    country: "Stockholm, Sweden",
    duration: "3 days",
    priceFrom: "€780",
    blurb: "Floating wood saunas, cold-plunge into the Baltic, candlelit dinners.",
    image: "https://images.unsplash.com/photo-1543429776-2782fc8e1acd?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Stockholm%20Archipelago%20Sauna%20Stockholm%2C%20Sweden",
  },
  {
    id: "valencia-festival",
    category: "wellness",
    title: "Valencia Festival Days",
    country: "Valencia, Spain",
    duration: "4 days",
    priceFrom: "€620",
    blurb: "Beach raves at Marina Beach Club, paella picnics and Las Arenas siestas.",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Valencia%20Marina%20Beach%20Club%20Festival%20Pass",
  },
  {
    id: "prague-spa",
    category: "wellness",
    title: "Prague Beer Spa & Bars",
    country: "Prague, Czech Republic",
    duration: "3 days",
    priceFrom: "€420",
    blurb: "Beer-tub spas by day, vaulted cellar clubs by night.",
    image: "https://images.unsplash.com/photo-1583422409516-2895a77efded?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Prague%20Beer%20Spa%20with%20Unlimited%20Beer",
  },
  {
    id: "split-croatia",
    category: "wellness",
    title: "Split Beach Energy",
    country: "Split, Croatia",
    duration: "5 days",
    priceFrom: "€820",
    blurb: "Adriatic boat parties, Roman-palace bars and quiet pebble coves.",
    image: "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Split%20Beach%20Energy%20Split%2C%20Croatia",
  },
  {
    id: "rhodes-resort",
    category: "wellness",
    title: "Rhodes Spa Resort",
    country: "Rhodes, Greece",
    duration: "5 days",
    priceFrom: "€880",
    blurb: "All-day pool service, hammam rituals and breezy beachfront dinners.",
    image: "https://images.unsplash.com/photo-1541849546-216549ae216d?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Rhodes%20Spa%20Resort%20Rhodes%2C%20Greece",
  },

  // ---------- ADVENTURE ----------
  {
    id: "dolomites",
    category: "adventure",
    title: "Dolomites Trekking",
    country: "Northern Italy",
    duration: "6 days",
    priceFrom: "€940",
    blurb: "Via Ferrata routes, rifugio dinners and the most surreal limestone spires in Europe.",
    image: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Dolomites%20Via%20Ferrata%20Guided%20Tour",
  },
  {
    id: "chamonix",
    category: "adventure",
    title: "Chamonix Alpinism",
    country: "French Alps",
    duration: "5 days",
    priceFrom: "€1,290",
    blurb: "Guided ice climbs under the white shoulder of Mont Blanc.",
    image: "https://images.unsplash.com/photo-1604147706283-d7119b5b822c?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Chamonix%20Mont%20Blanc%20Guided%20Summit%20Climb",
  },
  {
    id: "norway-fjord",
    category: "adventure",
    title: "Fjord Kayaking",
    country: "Western Norway",
    duration: "7 days",
    priceFrom: "€1,420",
    blurb: "Paddle past glaciers, hike to waterfalls, sleep in waterfront cabins.",
    image: "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Naeroyfjord%20Kayak%20Tour%20Flam%20Norway",
  },
  {
    id: "iceland-highlands",
    category: "adventure",
    title: "Icelandic Highlands",
    country: "Iceland",
    duration: "8 days",
    priceFrom: "€1,780",
    blurb: "Volcanic black sand, geothermal pools and the wildest hike in Europe.",
    image: "https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Landmannalaugar%20Highlands%20Hiking%20Day%20Tour",
  },
  {
    id: "matterhorn",
    category: "adventure",
    title: "Matterhorn Summit",
    country: "Zermatt, Switzerland",
    duration: "4 days",
    priceFrom: "€2,100",
    blurb: "The most iconic climb in the Alps with a certified mountain guide.",
    image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?q=80&w=1200&auto=format&fit=crop",
    partner: "TourRadar",
    link: "https://www.tourradar.com/search?q=Matterhorn%20Summit%20Zermatt%2C%20Switzerland",
  },
  {
    id: "scottish-highlands",
    category: "adventure",
    title: "Scottish Highlands Trail",
    country: "Scotland",
    duration: "6 days",
    priceFrom: "€820",
    blurb: "Heather moors, misty lochs and welcoming whisky distilleries.",
    image: "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?q=80&w=1200&auto=format&fit=crop",
    partner: "Intrepid Travel",
    link: "https://www.intrepidtravel.com/en/search?keywords=Glencoe%20Highland%20Cabin%20Scotland",
  },
  {
    id: "pyrenees",
    category: "adventure",
    title: "Pyrenees Traverse",
    country: "France / Spain",
    duration: "7 days",
    priceFrom: "€1,080",
    blurb: "Cross-border refuge hopping along Europe's wildest middle mountains.",
    image: "https://images.unsplash.com/photo-1591197172062-c718f82aba20?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Pyrenees%20Cross%20Border%20Hiking%20Tour",
  },
  {
    id: "picos-europa",
    category: "adventure",
    title: "Picos de Europa",
    country: "Northern Spain",
    duration: "5 days",
    priceFrom: "€820",
    blurb: "Limestone gorges, cave-aged cabrales cheese and almost no tourists.",
    image: "https://images.unsplash.com/photo-1531572753322-ad063cecc140?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Picos%20de%20Europa%20Cares%20Gorge%20Hike",
  },
  {
    id: "tatra",
    category: "adventure",
    title: "Tatra Mountains",
    country: "Poland",
    duration: "5 days",
    priceFrom: "€620",
    blurb: "Sharp alpine peaks, glacial lakes and steaming bowls of żurek.",
    image: "https://images.unsplash.com/photo-1592194996308-7b43878e84a6?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Tatra%20Mountains%20Morskie%20Oko%20Hike%20Zakopane",
  },
  {
    id: "carpathians",
    category: "adventure",
    title: "Carpathian Wilderness",
    country: "Romania",
    duration: "7 days",
    priceFrom: "€940",
    blurb: "Brown-bear country, painted monasteries and authentic Saxon villages.",
    image: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Carpathian%20Wilderness%20Brasov%20Bear%20Watching",
  },
  {
    id: "lofoten",
    category: "adventure",
    title: "Lofoten Islands",
    country: "Arctic Norway",
    duration: "8 days",
    priceFrom: "€2,180",
    blurb: "Razor-sharp peaks dropping straight into the Arctic sea — surfable.",
    image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Lofoten%20Islands%20Reine%20Hiking%20Tour",
  },
  {
    id: "faroe",
    category: "adventure",
    title: "Faroe Islands Trail",
    country: "Faroe Islands",
    duration: "6 days",
    priceFrom: "€1,680",
    blurb: "Storm-carved cliffs, grass-roof villages and 50 shades of green.",
    image: "https://images.unsplash.com/photo-1512438248247-f0f2a5a8b7f0?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Faroe%20Islands%20Mulafossur%20Sornfelli%20Hike",
  },
  {
    id: "triglav",
    category: "adventure",
    title: "Triglav National Park",
    country: "Slovenia",
    duration: "5 days",
    priceFrom: "€780",
    blurb: "Soča river kayaking, Julian Alps climbs and forest lodge nights.",
    image: "https://images.unsplash.com/photo-1529693662653-9d480530a697?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Soca%20River%20Kayak%20Tour%20Bovec%20Slovenia",
  },
  {
    id: "madeira-hike",
    category: "adventure",
    title: "Madeira Levada Hikes",
    country: "Madeira, Portugal",
    duration: "6 days",
    priceFrom: "€880",
    blurb: "Cloud-forest aqueduct trails, sea cliffs and stone-village stays.",
    image: "https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Madeira%20Levada%20do%20Caldeirao%20Verde%20Walk",
  },
  {
    id: "plitvice",
    category: "adventure",
    title: "Plitvice Falls Trek",
    country: "Croatia",
    duration: "3 days",
    priceFrom: "€460",
    blurb: "Cascading turquoise pools, wooden boardwalks and Balkan forest air.",
    image: "https://images.unsplash.com/photo-1593810450967-f9c42742e326?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Plitvice%20Lakes%20National%20Park%20Guided%20Hike",
  },
  {
    id: "snowdonia",
    category: "adventure",
    title: "Snowdonia Climbs",
    country: "Wales, UK",
    duration: "4 days",
    priceFrom: "€620",
    blurb: "Slate quarries, ridge walks and proper old-pub fires after.",
    image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Snowdon%20Summit%20Guided%20Hike%20Wales",
  },
  {
    id: "lake-district",
    category: "adventure",
    title: "Lake District Fells",
    country: "England, UK",
    duration: "4 days",
    priceFrom: "€580",
    blurb: "Wordsworth country with proper coffee, fell running and tea shops.",
    image: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?q=80&w=1200&auto=format&fit=crop",
    partner: "Intrepid Travel",
    link: "https://www.intrepidtravel.com/en/search?keywords=Windermere%20Lake%20District%20Cottage%20UK",
  },
  {
    id: "etna",
    category: "adventure",
    title: "Mount Etna Climb",
    country: "Sicily, Italy",
    duration: "3 days",
    priceFrom: "€520",
    blurb: "Black lava fields, summit craters and Etna-grown wine on the descent.",
    image: "https://images.unsplash.com/photo-1560662105-57f8ad6ae2d1?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Mount%20Etna%20Summit%20Crater%20Tour%20Sicily",
  },
  {
    id: "olympus",
    category: "adventure",
    title: "Mount Olympus Trek",
    country: "Greece",
    duration: "4 days",
    priceFrom: "€640",
    blurb: "Climb to the home of the Greek gods at 2,917 m.",
    image: "https://images.unsplash.com/photo-1563804447971-6e113ab80713?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Mount%20Olympus%20Mytikas%20Summit%20Climb%20Greece",
  },
  {
    id: "azores-adventure",
    category: "adventure",
    title: "Azores Volcano Trek",
    country: "Azores, Portugal",
    duration: "6 days",
    priceFrom: "€1,180",
    blurb: "Caldera lakes, lava tubes and whale-watching in the open Atlantic.",
    image: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?q=80&w=1200&auto=format&fit=crop",
    partner: "GetYourGuide",
    link: "https://www.getyourguide.com/en-us/s/?q=Sao%20Miguel%20Sete%20Cidades%20Lagoon%20Hike%20Azores",
  },
];

export const HERO_IMAGE =
  "https://images.unsplash.com/photo-1498994292978-4d6ff757c6dc?q=80&w=2400&auto=format&fit=crop";
